import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { Pencil, Trash2, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { DatePicker, ConfigProvider, theme as antTheme, Spin } from "antd";
import dayjs from "dayjs";
import { formatDisplayDate } from "../utils/dateFormat";
import { toast } from "react-toastify";
import { useDistrict } from "../utils/DistrictContext";
import { SwapOutlined } from "@ant-design/icons";
import { formatNumberUS } from "../utils/currencyUtils";
import {
  getMaintenanceLogs,
  addMaintenanceLog,
  deleteMaintenanceLog,
  updateMaintenanceLog,
  getTempMeterLogs,
  addTempMeterLog,
  updateTempMeterLog,
  deleteTempMeterLog,
} from "../services";
import moment from "moment";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import TempMeterTable from "../components/TempMeterTable";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function LeakRate() {
  const { theme, colors } = useTheme();
  const { selectedDistrict } = useDistrict();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [loading, setLoading] = useState(true);

  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showMaintenanceDeleteModal, setShowMaintenanceDeleteModal] =
    useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [maintenanceTotalFlow, setMaintenanceTotalFlow] = useState(0);

  const [maintenancePage, setMaintenancePage] = useState(1);
  const [maintenancePageSize, setMaintenancePageSize] = useState(5);
  const [maintenanceTotalPages, setMaintenanceTotalPages] = useState(1);
  const [maintenanceTotalRecords, setMaintenanceTotalRecords] = useState(0);

  const [tempMeterLogs, setTempMeterLogs] = useState([]);
  const [tempMeterPage, setTempMeterPage] = useState(1);
  const [tempMeterPageSize, setTempMeterPageSize] = useState(5);
  const [tempMeterTotalPages, setTempMeterTotalPages] = useState(1);
  const [tempMeterTotalRecords, setTempMeterTotalRecords] = useState(0);
  const [tempMeterTotalFlow, setTempMeterTotalFlow] = useState(0);
  const [selectedTempMeter, setSelectedTempMeter] = useState(null);

  const [rangeOption, setRangeOption] = useState("Past Day");
  const [intervalOption, setIntervalOption] = useState("Day");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterParams, setFilterParams] = useState({});

  const [leakLogs, setLeakLogs] = useState([]);
  const [leakPage, setLeakPage] = useState(1);
  const [leakPageSize, setLeakPageSize] = useState(5);
  const [leakTotalPages, setLeakTotalPages] = useState(1);
  const [leakTotalRecords, setLeakTotalRecords] = useState(0);
  const [leakTotalFlow, setLeakTotalFlow] = useState(0);

  const { RangePicker } = DatePicker;

  const handleDateChange = (dates, strings) => {
    setStartDate(strings[0]);
    setEndDate(strings[1]);
  };

  const disabledDate = (current) => {
    if (!current) return false;

    const usToday = dayjs().tz("America/New_York").format("YYYY-MM-DD");
    const currentDate = current.format("YYYY-MM-DD");

    return currentDate > usToday;
  };

  const [sortConfigMaintenance, setSortConfigMaintenance] = useState({
    key: null,
    direction: "asc",
  });

  const handleSortMaintenance = (key) => {
    let direction = "asc";
    if (
      sortConfigMaintenance.key === key &&
      sortConfigMaintenance.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfigMaintenance({ key, direction });
  };

  const sortedMaintenanceLogs = useMemo(() => {
    let data = [...maintenanceLogs];

    if (sortConfigMaintenance.key) {
      data.sort((a, b) => {
        const aValue = a[sortConfigMaintenance.key];
        const bValue = b[sortConfigMaintenance.key];
        if (aValue instanceof Date && bValue instanceof Date) {
          return sortConfigMaintenance.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfigMaintenance.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }
        return sortConfigMaintenance.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }
    return data;
  }, [maintenanceLogs, sortConfigMaintenance]);

  const [maintenanceForm, setMaintenanceForm] = useState({
    id: null,
    volume: "",
    category: "",
    location: "",
    date: null,

    workOrder: "",
    gpm: "",
    minutes: "",
    postFlushResidual: "",
    deadEnd: "",
    flushingDevice: "", // FV / BO
    meterId: "", // For Temp Meter
    registerNumber: "", // For Temp Meter
    meterSn: "", // For Temp Meter
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMaintenanceForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchMaintenanceLogs = async () => {
    try {
      const res = await getMaintenanceLogs(
        maintenancePage,
        maintenancePageSize,
        filterParams,
        "Maintainance",
      );

      if (res?.data?.status) {
        // const formatted = res.data.records.map((item) => ({
        //   id: item.id,
        //   volume: item.flow_value, // rename later in UI
        //   category: item.type === "Maintanence" ? "Maintenance" : item.type,
        //   location: item.location || "-", // NEW
        //   date: item.date ? new Date(item.date) : null, // SINGLE DATE
        // }));
        const formatted = res.data.records.map((item) => ({
          id: item.id,
          date: item.date ? new Date(item.date) : null,
          start_date: item.start_date || "-",
          end_date: item.end_date || "-",
          workOrder: item.work_order || "-",
          volume: item.flow_value,
          location: item.location || "-",
          gpm: item.flow_rate_value ?? "-",
          minutes: item.flush_duration ?? "-",
          category: item.type?.trim() || "-",
          // category: item.type === "Maintanence" ? "Maintenance" : item.type,
          postFlushResidual:
            item.post_flush_residual !== null ? item.post_flush_residual : "-",
          deadEnd: item.flush_reason || "-",
          flushingDevice: item.flushing_device || "-",
        }));

        setMaintenanceLogs(formatted);
        setMaintenanceTotalPages(res.data.total_pages);
        setMaintenanceTotalRecords(res.data.total_records);
        setMaintenanceTotalFlow(res.data.total_flow);
      }
    } catch (error) {
      console.error("Maintenance logs fetch failed:", error);
      toast.error("Failed to load maintenance logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeakLogs = async () => {
    try {
      const res = await getMaintenanceLogs(
        leakPage,
        leakPageSize,
        filterParams,
        "Leak",
      );

      if (res?.data?.status) {
        const formatted = res.data.records.map((item) => ({
          id: item.id,
          date: item.date ? new Date(item.date) : null,
          workOrder: item.work_order || "-",
          volume: item.flow_value,
          location: item.location || "-",
          gpm: item.flow_rate_value ?? "-",
          minutes: item.flush_duration ?? "-",
          category: item.type || "-",
          postFlushResidual:
            item.post_flush_residual !== null ? item.post_flush_residual : "-",
          deadEnd: item.flush_reason || "-",
          flushingDevice: item.flushing_device || "-",
        }));

        setLeakLogs(formatted);
        setLeakTotalPages(res.data.total_pages);
        setLeakTotalRecords(res.data.total_records);
        setLeakTotalFlow(res.data.total_flow);
      }
    } catch (error) {
      console.error("Leak logs fetch failed:", error);
    }
  };
  const fetchTempMeterLogs = async () => {
    try {
      const res = await getTempMeterLogs(
        tempMeterPage,
        tempMeterPageSize,
        filterParams,
      );

      if (res?.data?.status) {
        const formatted = res.data.records.map((item) => ({
          id: item.id,
          flow_value: item.flow_value,
          start_date: item.start_date || null,
          end_date: item.end_date || null,
          category: "Temp Meter",
          record_type: item.record_type || item.type || "-",
          location: item.location || "-",
          meter_id: item.meter_id || "-",
          register_number: item.register_number || "-",
          meter_sn: item.meter_sn || "-",
        }));

        setTempMeterLogs(formatted);
        setTempMeterTotalPages(res.data.total_pages);
        setTempMeterTotalRecords(res.data.total_records);
        setTempMeterTotalFlow(res.data.total_flow);
      }
    } catch (error) {
      console.error("Temp meter fetch failed:", error);
      toast.error("Failed to load Temp Meter logs");
    }
  };

  const handleAddMaintenance = async () => {
    const tempPayload = {
      // date: dayjs(maintenanceForm.date).format("YYYY-MM-DD"),
      flow_value: Number(maintenanceForm.volume),
      location: maintenanceForm.location || "",
      meter_id: maintenanceForm.meterId || "",
      register_number: maintenanceForm.registerNumber || "",
      meter_sn: maintenanceForm.meterSn || "",
      type: "Temp Meter",
      start_date: maintenanceForm.startDate
        ? dayjs(maintenanceForm.startDate).format("YYYY-MM-DD")
        : null,

      end_date: maintenanceForm.endDate
        ? dayjs(maintenanceForm.endDate).format("YYYY-MM-DD")
        : null,
    };

    try {
      // Check if category is "Temp Meter", then call only the temp meter API
      if (maintenanceForm.category === "Temp Meter") {
        const tempMeterResponse = await addTempMeterLog(tempPayload);

        // Ensure status is false (error), and message exists in response
        if (
          tempMeterResponse?.data?.status === false &&
          tempMeterResponse?.data?.message
        ) {
          toast.error(tempMeterResponse.data.data.message);
          return;
        }

        if (tempMeterResponse?.status || tempMeterResponse?.data?.status) {
          toast.success("Temp Meter added successfully");
          setShowMaintenanceModal(false);
          await fetchTempMeterLogs();
          return;
        }
      }

      if (
        !maintenanceForm.volume ||
        !maintenanceForm.category ||
        !maintenanceForm.date
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      const maintenancePayload = {
        date: dayjs(maintenanceForm.date).format("YYYY-MM-DD"),
        flow_value: Number(maintenanceForm.volume),
        location: maintenanceForm.location || "",
        work_order: maintenanceForm.workOrder || "",
        flow_rate_value: Number(maintenanceForm.gpm),
        flow_rate_unit: maintenanceForm.category === "Leak" ? "gph" : "gpm",
        flush_duration: Number(maintenanceForm.minutes),
        post_flush_residual: Number(maintenanceForm.postFlushResidual),
        flush_reason:
          maintenanceForm.deadEnd === "Dead End"
            ? "Dead End"
            : "Poor Water Quality",
        flushing_device:
          maintenanceForm.flushingDevice === "FV"
            ? "Fire Valve"
            : maintenanceForm.flushingDevice === "BO"
              ? "Blowoff"
              : "",
      };

      // Add Maintenance or Leak depending on category
      const maintenancePayloadWithType = {
        ...maintenancePayload,
        type:
          maintenanceForm.category === "Maintenance" ? "Maintainance" : "Leak",
      };

      // Call API for Maintenance or Leak
      const maintenanceResponse = await addMaintenanceLog(
        maintenancePayloadWithType,
      );

      if (maintenanceResponse?.status || maintenanceResponse?.data?.status) {
        toast.success(
          maintenanceForm.category === "Leak"
            ? "Leak log added successfully"
            : "Maintenance log added successfully",
        );
        setShowMaintenanceModal(false);

        if (maintenanceForm.category === "Leak") {
          await fetchLeakLogs();
        } else {
          await fetchMaintenanceLogs();
        }
      } else {
        // Handle error response from Maintenance or Leak API
        if (maintenanceResponse?.data?.message) {
          toast.error(maintenanceResponse?.data?.message); // Show error from Maintenance API
        }
      }
    } catch (error) {
      // Catch any errors not captured by the above logic
      toast.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const confirmDeleteMaintenance = async () => {
    try {
      // TEMP METER DELETE
      if (selectedMaintenance.category === "Temp Meter") {
        const res = await deleteTempMeterLog(selectedMaintenance.id);

        if (res?.status || res?.data?.status) {
          toast.success("Temp Meter deleted successfully");

          setShowMaintenanceDeleteModal(false);

          setSelectedMaintenance(null);

          await fetchTempMeterLogs();

          return;
        }
      }

      // MAINTENANCE / LEAK DELETE
      const res = await deleteMaintenanceLog(selectedMaintenance.id);

      if (res?.status || res?.data?.status) {
        toast.success(
          selectedMaintenance.category === "Leak"
            ? "Leak log deleted successfully"
            : "Maintenance log deleted successfully",
        );

        setShowMaintenanceDeleteModal(false);

        setSelectedMaintenance(null);

        if (selectedMaintenance.category === "Leak") {
          await fetchLeakLogs();
        } else {
          await fetchMaintenanceLogs();
        }
      }
    } catch (error) {
      console.error("Delete failed:", error);

      toast.error(error?.response?.data?.message || "Delete failed");
    }
  };

  const handleUpdateMaintenance = async () => {
    try {
      if (
        !maintenanceForm.volume ||
        !maintenanceForm.category ||
        !maintenanceForm.date
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      const payload = {
        date: dayjs(maintenanceForm.date).format("YYYY-MM-DD"),
        flow_value: Number(maintenanceForm.volume),
        location: maintenanceForm.location || "",
        work_order: maintenanceForm.workOrder || "",
        flow_rate_value: Number(maintenanceForm.gpm),
        flow_rate_unit: maintenanceForm.category === "Leak" ? "gph" : "gpm",
        flush_duration: Number(maintenanceForm.minutes),
        post_flush_residual: Number(maintenanceForm.postFlushResidual),
        flush_reason: maintenanceForm.deadEnd === "Yes" ? "Dead End" : "Other",
        flushing_device:
          maintenanceForm.flushingDevice === "FV"
            ? "Fire Valve"
            : maintenanceForm.flushingDevice === "BO"
              ? "Blowoff"
              : "",
        type:
          maintenanceForm.category === "Maintenance"
            ? "Maintainance"
            : maintenanceForm.category === "Leak"
              ? "Leak"
              : "",
      };

      const res = await updateMaintenanceLog(maintenanceForm.id, payload);

      if (res?.status || res?.data?.status) {
        toast.success(
          maintenanceForm.category === "Leak"
            ? "Leak log updated successfully"
            : "Maintenance log updated successfully",
        );
        setShowMaintenanceModal(false);
        if (maintenanceForm.category === "Leak") {
          await fetchLeakLogs();
        } else {
          await fetchMaintenanceLogs();
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const handleDeleteTempMeter = async () => {
    try {
      const res = await deleteTempMeterLog(selectedTempMeter.id);

      if (res?.status || res?.data?.status) {
        toast.success("Temp Meter deleted successfully");
        setShowDeleteConfirmation(false); // Close the modal
        setSelectedTempMeter(null); // Clear selected Temp Meter
        await fetchTempMeterLogs(); // Refresh the list of temp meters
      } else {
        toast.error(res?.data?.message || "Failed to delete Temp Meter");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete Temp Meter");
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await deleteTempMeterLog(selectedTempMeter.id);

      if (res?.status || res?.data?.status) {
        toast.success("Temp Meter deleted successfully");
        setShowDeleteConfirmation(false); // Close the modal
        setSelectedTempMeter(null); // Clear selected Temp Meter
        await fetchTempMeterLogs(); // Refresh the list of temp meters
      } else {
        toast.error(res?.data?.message || "Failed to delete Temp Meter");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete Temp Meter");
    }
  };

  useEffect(() => {
    fetchMaintenanceLogs();
    fetchLeakLogs();
    fetchTempMeterLogs();
  }, [
    maintenancePage,
    maintenancePageSize,
    leakPage,
    leakPageSize,
    tempMeterPage,
    tempMeterPageSize,
    selectedDistrict,
    filterParams,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full pt-5">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <div className="flex justify-end">
        <button
          onClick={() => {
            setMaintenanceForm({
              id: null,
              volume: "",
              category: "",
              location: "",
              date: null,
            });
            setShowMaintenanceModal(true);
          }}
          className="px-5 py-2.5 rounded-lg text-white font-semibold shadow-lg"
          style={{ backgroundImage: colors.gradient }}
        >
          + Add Items
        </button>
      </div>

      {/* Maintenance Log */}
      <div
        className="backdrop-blur-sm rounded-xl border overflow-hidden"
        style={{
          backgroundColor: colors.cardBg,
          borderColor: colors.border,
        }}
      >
        <div
          className="p-6 border-b flex items-center justify-between flex-wrap gap-4"
          style={{ borderColor: colors.border }}
        >
          <h2
            className="text-xl font-bold text-transparent bg-clip-text"
            style={{
              backgroundImage: colors.gradient,
              WebkitTextFillColor: "transparent",
            }}
          >
            Maintenance Logs
          </h2>
          {/*  */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1">
                {/* <label
                  className="text-xs mb-2 block"
                  style={{ color: colors.primary }}
                >
                  Range | Interval | Custom
                </label> */}
                <div
                  className="flex items-center gap-1 p-1 rounded-xl border w-full justify-between flex-wrap"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                  }}
                >
                  {/* --- SECTION 1: RANGE PRESETS --- */}
                  <div className="flex items-center gap-1 flex-wrap">
                    {[
                      { label: "D", value: "past_day" },
                      { label: "W", value: "past_week" },
                      { label: "1M", value: "past_month" },
                      { label: "3M", value: "past_3_month" },
                      { label: "6M", value: "past_6_month" },
                      { label: "YTD", value: "current_year" },
                      { label: "1Y", value: "last_1_year" },
                    ].map((tp) => {
                      const isYTD =
                        tp.value === "YTD" &&
                        startDate ===
                          moment().startOf("year").format("YYYY-MM-DD");

                      const isActive = rangeOption === tp.value || isYTD; // ✅ FIX

                      return (
                        <button
                          key={tp.value}
                          onClick={() => {
                            if (tp.value === "current_year") {
                              const startOfYear = moment()
                                .startOf("year")
                                .format("YYYY-MM-DD");
                              const today = moment().format("YYYY-MM-DD");

                              setStartDate(startOfYear);
                              setEndDate(today);

                              setFilterParams({
                                option: "custom",
                                start_date: startOfYear,
                                end_date: today,
                              });

                              setRangeOption("Custom");
                            } else {
                              setRangeOption(tp.value);

                              setFilterParams({
                                option: tp.value,
                              });

                              setStartDate(null);
                              setEndDate(null);
                            }

                            setMaintenancePage(1); // reset page
                          }}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold"
                          style={{
                            backgroundColor: isActive
                              ? `${colors.primary}20`
                              : "transparent",
                            color: isActive
                              ? colors.primary
                              : colors.textSecondary,
                            border: isActive
                              ? `1px solid ${colors.primary}40`
                              : "1px solid transparent",
                          }}
                        >
                          {tp.label}
                        </button>
                      );
                    })}
                  </div>
                  <div
                    className="h-6 w-[1px] mx-1"
                    style={{ backgroundColor: colors.border }}
                  />

                  {/* --- SECTION 3: CALENDAR --- */}
                  <ConfigProvider
                    theme={{
                      algorithm:
                        theme === "white"
                          ? antTheme.defaultAlgorithm
                          : antTheme.darkAlgorithm,
                      token: {
                        colorPrimary: colors.primary,
                        controlHeight: 32,
                        borderRadius: 8,
                      },
                    }}
                  >
                    <RangePicker
                      value={[
                        startDate ? dayjs(startDate, "YYYY-MM-DD") : null,
                        endDate ? dayjs(endDate, "YYYY-MM-DD") : null,
                      ]}
                      onChange={(dates, strings) => {
                        handleDateChange(dates, strings);

                        setFilterParams({
                          option: "custom",
                          start_date: strings[0],
                          end_date: strings[1],
                        });

                        setRangeOption("Custom");
                        setMaintenancePage(1);
                      }}
                      disabledDate={disabledDate}
                      placeholder={["Start", "End"]}
                      variant="borderless"
                      separator={
                        <SwapOutlined style={{ color: colors.textSecondary }} />
                      }
                      className="custom-range-picker cursor-pointer"
                      style={{ width: "240px" }}
                      classNames={{
                        popup: {
                          root: `mizu-calendar-popup calendar-${theme}`,
                        },
                      }}
                    />
                  </ConfigProvider>
                </div>
              </div>
              <div className="">
                <label
                  className="text-xs mb-2 block"
                  style={{ color: colors.primary }}
                >
                  Total Flow
                </label>
                <div
                  className="flex items-center gap-1 p-1 rounded-xl border justify-between"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    width: "fit-content",
                  }}
                >
                  <h5
                    style={{ color: colors.textSecondary }}
                    className="px-4 py-1"
                  >
                    {" "}
                    {formatNumberUS(maintenanceTotalFlow)}
                  </h5>
                </div>
              </div>
            </div>

            {/*  */}

            {/* <button
              onClick={() => {
                setMaintenanceForm({
                  id: null,
                  volume: "",
                  category: "",
                  location: "",
                  date: null,
                });
                setShowMaintenanceModal(true);
              }}
              className="px-4 py-2 rounded-lg text-white font-semibold"
              style={{ backgroundImage: colors.gradient }}
            >
              + Add Maintenance
            </button> */}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr
                className="border-b"
                style={{
                  borderColor: colors.border,
                  backgroundColor: `${colors.background}80`,
                }}
              >
                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("date")}
                >
                  <span className="flex items-center gap-2">
                    Date
                    {sortConfigMaintenance.key === "date" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                {/* WORK ORDER */}
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-2">Work Order</span>
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("volume")}
                >
                  <span className="flex items-center gap-2">
                    Water Usage (Gallons)
                    {sortConfigMaintenance.key === "volume" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("location")}
                >
                  <span className="flex items-center gap-2">
                    Location
                    {sortConfigMaintenance.key === "location" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                {/* GPM */}
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-2">GPM</span>
                </th>

                {/* MINUTES */}
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-2">Minutes</span>
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("category")}
                >
                  <span className="flex items-center gap-2">
                    Category
                    {sortConfigMaintenance.key === "category" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                {/* POST FLUSH */}

                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-2">
                    Post-Flush Residual
                  </span>
                </th>

                {/* DEAD END */}
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-2">DE/PWQ</span>
                </th>

                {/* DEVICE */}
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-2">
                    Flushing Device
                  </span>
                </th>

                <th
                  className="px-6 py-4 text-center text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {maintenanceLogs.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="text-center py-6"
                    style={{ color: colors.textSecondary }}
                  >
                    No maintenance logs found
                  </td>
                </tr>
              ) : (
                sortedMaintenanceLogs.map((item, index) => (
                  <motion.tr
                    key={item.id}
                    className="border-b"
                    style={{ borderColor: colors.border }}
                  >
                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.date ? formatDisplayDate(item.date) : "-"}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.workOrder}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {/* {item.volume} */}
                      {formatNumberUS(item.volume)}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.location}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.gpm}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.minutes}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.category}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.postFlushResidual}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.deadEnd}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.flushingDevice}
                    </td>

                    <td className="px-6 py-4 text-center flex justify-center gap-5">
                      <button
                        onClick={() => {
                          setMaintenanceForm({
                            id: item.id,
                            volume: item.volume,
                            category: item.category,
                            location: item.location,
                            date: item.date,

                            workOrder: item.workOrder,
                            gpm: item.gpm,
                            minutes: item.minutes,
                            postFlushResidual: item.postFlushResidual,
                            deadEnd:
                              item.deadEnd === "Dead End"
                                ? "Yes"
                                : item.deadEnd
                                  ? "No"
                                  : "",

                            flushingDevice:
                              item.flushingDevice === "Fire Valve"
                                ? "FV"
                                : item.flushingDevice === "Blowoff"
                                  ? "BO"
                                  : "",
                          });

                          setShowMaintenanceModal(true);
                        }}
                      >
                        <Pencil className="w-4 h-4 text-blue-400" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMaintenance(item);
                          setShowMaintenanceDeleteModal(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
          <div
            className="flex items-center justify-between p-4 border-t"
            // style={{
            //   borderColor: colors.border,
            //   backgroundColor: colors.cardBg,
            // }}
            style={{ color: colors.textSecondary }}
          >
            {/* LEFT TEXT */}
            <div className="text-xs" style={{ color: colors.textSecondary }}>
              Showing {(maintenancePage - 1) * maintenancePageSize + 1} to{" "}
              {Math.min(
                maintenancePage * maintenancePageSize,
                maintenanceTotalRecords,
              )}{" "}
              of {maintenanceTotalRecords} records
            </div>

            <div className="flex items-center gap-4">
              <button
                disabled={maintenancePage === 1}
                onClick={() => setMaintenancePage((p) => Math.max(p - 1, 1))}
              >
                Prev
              </button>

              <select
                value={maintenancePage}
                onChange={(e) => setMaintenancePage(Number(e.target.value))}
              >
                {Array.from(
                  { length: maintenanceTotalPages },
                  (_, i) => i + 1,
                ).map((page) => (
                  <option key={page} value={page}>
                    {page}
                  </option>
                ))}
              </select>

              {/* NEXT */}
              <button
                disabled={maintenancePage === maintenanceTotalPages}
                onClick={() =>
                  setMaintenancePage((p) =>
                    Math.min(p + 1, maintenanceTotalPages),
                  )
                }
              >
                Next
              </button>
              {/* PAGE SIZE */}
              <select
                value={maintenancePageSize}
                onChange={(e) => {
                  setMaintenancePageSize(Number(e.target.value));
                  setMaintenancePage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Leak Log */}
      <div
        className="backdrop-blur-sm rounded-xl border overflow-hidden"
        style={{
          backgroundColor: colors.cardBg,
          borderColor: colors.border,
        }}
      >
        <div
          className="p-6 border-b flex items-center justify-between flex-wrap gap-4"
          style={{ borderColor: colors.border }}
        >
          <h2
            className="text-xl font-bold text-transparent bg-clip-text"
            style={{
              backgroundImage: colors.gradient,
              WebkitTextFillColor: "transparent",
            }}
          >
            Leak Logs
          </h2>
          {/*  */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1">
                {/* <label
                  className="text-xs mb-2 block"
                  style={{ color: colors.primary }}
                >
                  Range | Interval | Custom
                </label> */}
                <div
                  className="flex items-center gap-1 p-1 rounded-xl border w-full justify-between flex-wrap"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                  }}
                >
                  <div className="flex items-center gap-1 flex-wrap">
                    {[
                      { label: "D", value: "past_day" },
                      { label: "W", value: "past_week" },
                      { label: "1M", value: "past_month" },
                      { label: "3M", value: "past_3_month" },
                      { label: "6M", value: "past_6_month" },
                      { label: "YTD", value: "current_year" },
                      { label: "1Y", value: "last_1_year" },
                    ].map((tp) => {
                      const isYTD =
                        tp.value === "YTD" &&
                        startDate ===
                          moment().startOf("year").format("YYYY-MM-DD");

                      const isActive = rangeOption === tp.value || isYTD; // ✅ FIX

                      return (
                        <button
                          key={tp.value}
                          onClick={() => {
                            if (tp.value === "current_year") {
                              const startOfYear = moment()
                                .startOf("year")
                                .format("YYYY-MM-DD");
                              const today = moment().format("YYYY-MM-DD");

                              setStartDate(startOfYear);
                              setEndDate(today);

                              setFilterParams({
                                option: "custom",
                                start_date: startOfYear,
                                end_date: today,
                              });

                              setRangeOption("Custom");
                            } else {
                              setRangeOption(tp.value);

                              setFilterParams({
                                option: tp.value,
                              });

                              setStartDate(null);
                              setEndDate(null);
                            }

                            setLeakPage(1); // reset page
                          }}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold"
                          style={{
                            backgroundColor: isActive
                              ? `${colors.primary}20`
                              : "transparent",
                            color: isActive
                              ? colors.primary
                              : colors.textSecondary,
                            border: isActive
                              ? `1px solid ${colors.primary}40`
                              : "1px solid transparent",
                          }}
                        >
                          {tp.label}
                        </button>
                      );
                    })}
                  </div>
                  <div
                    className="h-6 w-[1px] mx-1"
                    style={{ backgroundColor: colors.border }}
                  />

                  {/* --- SECTION 3: CALENDAR --- */}
                  <ConfigProvider
                    theme={{
                      algorithm:
                        theme === "white"
                          ? antTheme.defaultAlgorithm
                          : antTheme.darkAlgorithm,
                      token: {
                        colorPrimary: colors.primary,
                        controlHeight: 32,
                        borderRadius: 8,
                      },
                    }}
                  >
                    <RangePicker
                      value={[
                        startDate ? dayjs(startDate, "YYYY-MM-DD") : null,
                        endDate ? dayjs(endDate, "YYYY-MM-DD") : null,
                      ]}
                      onChange={(dates, strings) => {
                        handleDateChange(dates, strings);

                        setFilterParams({
                          option: "custom",
                          start_date: strings[0],
                          end_date: strings[1],
                        });

                        setRangeOption("Custom");
                        setLeakPage(1);
                      }}
                      disabledDate={disabledDate}
                      placeholder={["Start", "End"]}
                      variant="borderless"
                      separator={
                        <SwapOutlined style={{ color: colors.textSecondary }} />
                      }
                      className="custom-range-picker cursor-pointer"
                      style={{ width: "240px" }}
                      classNames={{
                        popup: {
                          root: `mizu-calendar-popup calendar-${theme}`,
                        },
                      }}
                    />
                  </ConfigProvider>
                </div>
              </div>
              <div className="">
                <label
                  className="text-xs mb-2 block"
                  style={{ color: colors.primary }}
                >
                  Total Flow
                </label>
                <div
                  className="flex items-center gap-1 p-1 rounded-xl border justify-between"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    width: "fit-content",
                  }}
                >
                  <h5
                    style={{ color: colors.textSecondary }}
                    className="px-4 py-1"
                  >
                    {" "}
                    {formatNumberUS(leakTotalFlow)}
                  </h5>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr
                className="border-b"
                style={{
                  borderColor: colors.border,
                  backgroundColor: `${colors.background}80`,
                }}
              >
                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("date")}
                >
                  <span className="flex items-center gap-2">
                    Date
                    {sortConfigMaintenance.key === "date" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                {/* WORK ORDER */}
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-2">Work Order</span>
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("volume")}
                >
                  <span className="flex items-center gap-2">
                    Water Usage (Gallons)
                    {sortConfigMaintenance.key === "volume" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("location")}
                >
                  <span className="flex items-center gap-2">
                    Location
                    {sortConfigMaintenance.key === "location" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                {/* GPM */}
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-2">GPM</span>
                </th>

                {/* MINUTES */}
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-2">Minutes</span>
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold cursor-pointer"
                  style={{ color: colors.textSecondary }}
                  onClick={() => handleSortMaintenance("category")}
                >
                  <span className="flex items-center gap-2">
                    Category
                    {sortConfigMaintenance.key === "category" ? (
                      sortConfigMaintenance.direction === "asc" ? (
                        <ArrowUp
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      ) : (
                        <ArrowDown
                          className="w-4 h-4"
                          style={{ color: colors.primary }}
                        />
                      )
                    ) : (
                      <ArrowUpDown
                        className="w-4 h-4"
                        style={{ color: colors.primary }}
                      />
                    )}
                  </span>
                </th>

                <th
                  className="px-6 py-4 text-center text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {leakLogs.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="text-center py-6"
                    style={{ color: colors.textSecondary }}
                  >
                    No leak logs found
                  </td>
                </tr>
              ) : (
                leakLogs.map((item, index) => (
                  <motion.tr
                    key={item.id}
                    className="border-b"
                    style={{ borderColor: colors.border }}
                  >
                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.date ? formatDisplayDate(item.date) : "-"}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.workOrder}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {formatNumberUS(item.volume)}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.location}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.gpm}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.minutes}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.category}
                    </td>

                    <td className="px-6 py-4 text-center flex justify-center gap-5">
                      <button
                        onClick={() => {
                          setMaintenanceForm({
                            id: item.id,
                            volume: item.volume,
                            category: item.category,
                            location: item.location,
                            date: item.date,

                            workOrder: item.workOrder,
                            gpm: item.gpm,
                            minutes: item.minutes,
                            postFlushResidual: item.postFlushResidual,
                            deadEnd:
                              item.deadEnd === "Dead End"
                                ? "Yes"
                                : item.deadEnd
                                  ? "No"
                                  : "",

                            flushingDevice:
                              item.flushingDevice === "Fire Valve"
                                ? "FV"
                                : item.flushingDevice === "Blowoff"
                                  ? "BO"
                                  : "",
                          });

                          setShowMaintenanceModal(true);
                        }}
                      >
                        <Pencil className="w-4 h-4 text-blue-400" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMaintenance(item);
                          setShowMaintenanceDeleteModal(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
          <div
            className="flex items-center justify-between p-4 border-t"
            style={{ color: colors.textSecondary }}
          >
            {/* LEFT TEXT */}
            <div className="text-xs" style={{ color: colors.textSecondary }}>
              Showing {(leakPage - 1) * leakPageSize + 1} to{" "}
              {Math.min(leakPage * leakPageSize, leakTotalRecords)} of{" "}
              {leakTotalRecords} records
            </div>

            <div className="flex items-center gap-4">
              <button
                disabled={leakPage === 1}
                onClick={() => setLeakPage((p) => Math.max(p - 1, 1))}
              >
                Prev
              </button>

              <select
                value={leakPage}
                onChange={(e) => setLeakPage(Number(e.target.value))}
              >
                {Array.from({ length: leakTotalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <option key={page} value={page}>
                      {page}
                    </option>
                  ),
                )}
              </select>

              {/* NEXT */}
              <button
                disabled={leakPage === leakTotalPages}
                onClick={() =>
                  setLeakPage((p) => Math.min(p + 1, leakTotalPages))
                }
              >
                Next
              </button>
              {/* PAGE SIZE */}
              <select
                value={leakPageSize}
                onChange={(e) => {
                  setLeakPageSize(Number(e.target.value));
                  setLeakPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Temp Meter */}
      <TempMeterTable
        tempMeterLogs={tempMeterLogs}
        setSelectedTempMeter={setSelectedTempMeter}
        setShowDeleteConfirmation={setShowDeleteConfirmation}
        tempMeterPage={tempMeterPage}
        tempMeterPageSize={tempMeterPageSize}
        tempMeterTotalPages={tempMeterTotalPages}
        tempMeterTotalFlow={tempMeterTotalFlow}
        tempMeterTotalRecords={tempMeterTotalRecords}
        startDate={startDate}
        endDate={endDate}
        rangeOption={rangeOption}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        setRangeOption={setRangeOption}
        setFilterParams={setFilterParams}
        setTempMeterPage={setTempMeterPage}
        setTempMeterPageSize={setTempMeterPageSize}
        handleDateChange={handleDateChange}
        disabledDate={disabledDate}
      />

      {showMaintenanceModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          style={{ backgroundColor: colors.modalOverlay }}
        >
          <motion.div
            className="w-full max-w-lg backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border my-10 max-h-[90vh] flex flex-col max-[767px]:my-4"
            style={{
              background: colors.inputBg,
              borderColor: colors.border,
            }}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className="p-6 border-b flex justify-between items-center"
              style={{ borderColor: colors.border }}
            >
              {/* <h2
                className="text-xl font-bold text-transparent bg-clip-text"
                style={{
                  backgroundImage: colors.gradient,
                  WebkitTextFillColor: "transparent",
                }}
              >
                {maintenanceForm.category === "Temp Meter"
                  ? maintenanceForm.id
                    ? "Edit Temp Meter"
                    : "Add Temp Meter"
                  : maintenanceForm.category === "Leak"
                    ? "Edit Leak Log"
                    : maintenanceForm.id
                      ? "Edit Maintenance Log"
                      : "Add Maintenance Log"}
              </h2> */}
              <h2
                className="text-xl font-bold text-transparent bg-clip-text"
                style={{
                  backgroundImage: colors.gradient,
                  WebkitTextFillColor: "transparent",
                }}
              >
                {maintenanceForm.category === "Temp Meter"
                  ? maintenanceForm.id
                    ? "Edit Temp Meter"
                    : "Add Temp Meter"
                  : maintenanceForm.category === "Leak"
                    ? maintenanceForm.id
                      ? "Edit Leak Log"
                      : "Add Leak Log"
                    : maintenanceForm.id
                      ? "Edit Maintenance Log"
                      : "Add Maintenance Log"}
              </h2>
              <button
                onClick={() => {
                  setShowMaintenanceModal(false);
                  setMaintenanceForm({
                    id: null,
                    volume: "",
                    category: "",
                    location: "",
                    date: null,
                  });
                }}
                className="text-xl transition"
                style={{ color: colors.textSecondary }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = colors.text)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = colors.textSecondary)
                }
              >
                ✕
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label
                  className="text-xs font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Volume(Gallons)
                </label>
                <input
                  type="text"
                  value={maintenanceForm.volume}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,6}$/.test(value)) {
                      setMaintenanceForm({
                        ...maintenanceForm,
                        volume: value,
                      });
                    }
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="px-4 py-2.5 rounded-lg border outline-none"
                  style={{
                    backgroundColor: `${colors.background}80`,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                />
              </div>

              {!maintenanceForm.id && (
                <div className="flex flex-col gap-2">
                  <label
                    className="text-xs font-medium"
                    style={{ color: colors.textSecondary }}
                  >
                    Category
                  </label>
                  <div className="flex gap-4">
                    {["Maintenance", "Leak", "Temp Meter"].map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          checked={maintenanceForm.category === type}
                          onChange={() => {
                            const newCategory =
                              maintenanceForm.category === type ? "" : type;

                            setMaintenanceForm({
                              ...maintenanceForm,
                              category: newCategory,
                              ...(newCategory === "Temp Meter" && {
                                workOrder: "",
                                gpm: "",
                                minutes: "",
                                postFlushResidual: "",
                                deadEnd: "",
                                flushingDevice: "",
                              }),
                            });
                          }}
                        />
                        <span style={{ color: colors.text }}>{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <label
                  className="text-xs font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Location
                </label>
                <input
                  value={maintenanceForm.location}
                  onChange={(e) =>
                    setMaintenanceForm({
                      ...maintenanceForm,
                      location: e.target.value,
                    })
                  }
                  className="px-4 py-2.5 rounded-lg border outline-none"
                  style={{
                    backgroundColor: `${colors.background}80`,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                />
              </div>

              {maintenanceForm.category !== "Temp Meter" && (
                <>
                  <div className="flex flex-col gap-2">
                    <label
                      className="text-xs font-medium"
                      style={{ color: colors.textSecondary }}
                    >
                      Date
                    </label>

                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div
                          className="border rounded-lg px-0 py-0"
                          style={{
                            background: colors.cardBg,
                            border: `1px solid ${colors.border}`,
                          }}
                        >
                          <ConfigProvider
                            theme={{
                              token: {
                                colorPrimary: colors.primary,
                                colorText: colors.text,
                                colorTextSecondary: colors.textSecondary,
                                colorBorder: colors.border,
                                colorBgContainer: colors.background,
                              },
                              components: {
                                DatePicker: {
                                  colorBgContainer: colors.background,
                                  colorBgElevated: colors.cardBg,
                                  colorText: colors.text,
                                  colorTextHeading: colors.text,
                                  colorTextDisabled: colors.textSecondary,
                                  cellHoverBg: colors.primary + "20",
                                  cellActiveWithRangeBg: colors.primary + "30",
                                  cellRangeBorderColor: colors.primary,
                                  activeBorderColor: colors.primary,
                                  hoverBorderColor: colors.primary,
                                },
                              },
                            }}
                          >
                            <DatePicker
                              picker="date"
                              className="w-full rounded-lg"
                              style={{ padding: "8px 12px" }}
                              value={
                                maintenanceForm.date
                                  ? dayjs(maintenanceForm.date)
                                  : null
                              }
                              disabledDate={disabledDate}
                              onChange={(date) =>
                                setMaintenanceForm({
                                  ...maintenanceForm,
                                  date: date?.toDate(),
                                })
                              }
                            />
                          </ConfigProvider>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      className="text-xs"
                      style={{ color: colors.textSecondary }}
                    >
                      Work Order
                    </label>
                    <input
                      value={maintenanceForm.workOrder}
                      onChange={(e) =>
                        setMaintenanceForm({
                          ...maintenanceForm,
                          workOrder: e.target.value,
                        })
                      }
                      className="px-4 py-2.5 rounded-lg border outline-none"
                      style={{
                        backgroundColor: `${colors.background}80`,
                        borderColor: colors.border,
                        color: colors.text,
                      }}
                    />
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1 flex flex-col gap-2">
                      <label
                        className="text-xs"
                        style={{ color: colors.textSecondary }}
                      >
                        {maintenanceForm.category === "Leak" ? "GPH" : "GPM"}
                      </label>
                      <input
                        value={maintenanceForm.gpm}
                        onChange={(e) =>
                          setMaintenanceForm({
                            ...maintenanceForm,
                            gpm: e.target.value,
                          })
                        }
                        className="px-4 py-2.5 rounded-lg border outline-none"
                        style={{
                          backgroundColor: `${colors.background}80`,
                          borderColor: colors.border,
                          color: colors.text,
                        }}
                      />
                    </div>

                    <div className="flex-1 flex flex-col gap-2">
                      <label
                        className="text-xs"
                        style={{ color: colors.textSecondary }}
                      >
                        {maintenanceForm.category === "Leak"
                          ? "Hours"
                          : "Minutes"}
                      </label>
                      <input
                        value={maintenanceForm.minutes}
                        onChange={(e) =>
                          setMaintenanceForm({
                            ...maintenanceForm,
                            minutes: e.target.value,
                          })
                        }
                        className="px-4 py-2.5 rounded-lg border outline-none"
                        style={{
                          backgroundColor: `${colors.background}80`,
                          borderColor: colors.border,
                          color: colors.text,
                        }}
                      />
                    </div>
                  </div>
                  {maintenanceForm.category !== "Leak" && (
                    <>
                      <div className="flex flex-col gap-2">
                        <label
                          className="text-xs"
                          style={{ color: colors.textSecondary }}
                        >
                          Post Flush Residual
                        </label>
                        <input
                          type="number"
                          value={maintenanceForm.postFlushResidual}
                          onChange={(e) =>
                            setMaintenanceForm({
                              ...maintenanceForm,
                              postFlushResidual: e.target.value,
                            })
                          }
                          className="px-4 py-2.5 rounded-lg border outline-none"
                          style={{
                            backgroundColor: `${colors.background}80`,
                            borderColor: colors.border,
                            color: colors.text,
                          }}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <label
                          className="text-xs"
                          style={{ color: colors.textSecondary }}
                        >
                          DE & PWQ
                        </label>

                        <select
                          value={maintenanceForm.deadEnd}
                          onChange={(e) =>
                            setMaintenanceForm({
                              ...maintenanceForm,
                              deadEnd: e.target.value,
                            })
                          }
                          className="px-4 py-2.5 rounded-lg border outline-none"
                          style={{
                            backgroundColor: `${colors.background}80`,
                            borderColor: colors.border,
                            color: colors.text,
                          }}
                        >
                          <option value="">Select</option>
                          <option value="Yes">Dead End</option>
                          <option value="No">Poor Water Quality</option>
                        </select>
                      </div>

                      <div className="flex flex-col gap-2">
                        <label
                          className="text-xs"
                          style={{ color: colors.textSecondary }}
                        >
                          Flushing Device
                        </label>

                        <select
                          value={maintenanceForm.flushingDevice}
                          onChange={(e) =>
                            setMaintenanceForm({
                              ...maintenanceForm,
                              flushingDevice: e.target.value,
                            })
                          }
                          className="px-4 py-2.5 rounded-lg border outline-none"
                          style={{
                            backgroundColor: `${colors.background}80`,
                            borderColor: colors.border,
                            color: colors.text,
                          }}
                        >
                          <option value="">Select Device</option>
                          <option value="FV">Fire Value</option>
                          <option value="BO">Blow Off</option>
                          <option value="HY">Hydrant</option>
                        </select>
                      </div>
                    </>
                  )}
                </>
              )}
              {maintenanceForm.category === "Temp Meter" && (
                <>
                  <div className="flex flex-col gap-2">
                    <label
                      className="text-xs font-medium"
                      style={{ color: colors.textSecondary }}
                    >
                      Start Date
                    </label>

                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div
                          className="border rounded-lg px-0 py-0"
                          style={{
                            background: colors.cardBg,
                            border: `1px solid ${colors.border}`,
                          }}
                        >
                          <ConfigProvider
                            theme={{
                              token: {
                                colorPrimary: colors.primary,
                                colorText: colors.text,
                                colorTextSecondary: colors.textSecondary,
                                colorBorder: colors.border,
                                colorBgContainer: colors.background,
                              },
                              components: {
                                DatePicker: {
                                  colorBgContainer: colors.background,
                                  colorBgElevated: colors.cardBg,
                                  colorText: colors.text,
                                  colorTextHeading: colors.text,
                                  colorTextDisabled: colors.textSecondary,
                                  cellHoverBg: colors.primary + "20",
                                  cellActiveWithRangeBg: colors.primary + "30",
                                  cellRangeBorderColor: colors.primary,
                                  activeBorderColor: colors.primary,
                                  hoverBorderColor: colors.primary,
                                },
                              },
                            }}
                          >
                            <DatePicker
                              picker="date"
                              className="w-full rounded-lg"
                              style={{ padding: "8px 12px" }}
                              // value={
                              //   maintenanceForm.startDate
                              //     ? dayjs(maintenanceForm.startDate)
                              //     : null
                              // }
                              value={maintenanceForm.startDate || null}
                              disabledDate={disabledDate}
                              onChange={(date) =>
                                setMaintenanceForm((prev) => ({
                                  ...prev,
                                  startDate: date,
                                }))
                              }
                            />
                          </ConfigProvider>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      className="text-xs font-medium"
                      style={{ color: colors.textSecondary }}
                    >
                      End Date
                    </label>

                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div
                          className="border rounded-lg px-0 py-0"
                          style={{
                            background: colors.cardBg,
                            border: `1px solid ${colors.border}`,
                          }}
                        >
                          <ConfigProvider
                            theme={{
                              token: {
                                colorPrimary: colors.primary,
                                colorText: colors.text,
                                colorTextSecondary: colors.textSecondary,
                                colorBorder: colors.border,
                                colorBgContainer: colors.background,
                              },
                              components: {
                                DatePicker: {
                                  colorBgContainer: colors.background,
                                  colorBgElevated: colors.cardBg,
                                  colorText: colors.text,
                                  colorTextHeading: colors.text,
                                  colorTextDisabled: colors.textSecondary,
                                  cellHoverBg: colors.primary + "20",
                                  cellActiveWithRangeBg: colors.primary + "30",
                                  cellRangeBorderColor: colors.primary,
                                  activeBorderColor: colors.primary,
                                  hoverBorderColor: colors.primary,
                                },
                              },
                            }}
                          >
                            <DatePicker
                              picker="date"
                              className="w-full rounded-lg"
                              style={{ padding: "8px 12px" }}
                              value={
                                maintenanceForm.endDate
                                  ? dayjs(maintenanceForm.endDate)
                                  : null
                              }
                              disabledDate={disabledDate}
                              onChange={(date) =>
                                setMaintenanceForm((prev) => ({
                                  ...prev,
                                  endDate: date,
                                }))
                              }
                            />
                          </ConfigProvider>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      className="text-xs"
                      style={{ color: colors.textSecondary }}
                    >
                      Meter ID
                    </label>
                    <input
                      type="text"
                      name="meterId"
                      value={maintenanceForm.meterId}
                      onChange={(e) =>
                        setMaintenanceForm({
                          ...maintenanceForm,
                          meterId: e.target.value,
                        })
                      }
                      className="px-4 py-2.5 rounded-lg border outline-none"
                      style={{
                        backgroundColor: `${colors.background}80`,
                        borderColor: colors.border,
                        color: colors.text,
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      className="text-xs"
                      style={{ color: colors.textSecondary }}
                    >
                      Register Number
                    </label>
                    <select
                      value={maintenanceForm.registerNumber}
                      name="registerNumber"
                      onChange={(e) =>
                        setMaintenanceForm({
                          ...maintenanceForm,
                          registerNumber: e.target.value,
                        })
                      }
                      className="px-4 py-2.5 rounded-lg border outline-none"
                      style={{
                        backgroundColor: `${colors.background}80`,
                        borderColor: colors.border,
                        color: colors.text,
                      }}
                    >
                      <option value="none">None</option>
                      <option value="L">L</option>
                      <option value="H">H</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      className="text-xs"
                      style={{ color: colors.textSecondary }}
                    >
                      Meter SN
                    </label>
                    <input
                      type="text"
                      name="meterSn"
                      value={maintenanceForm.meterSn}
                      onChange={(e) =>
                        setMaintenanceForm({
                          ...maintenanceForm,
                          meterSn: e.target.value,
                        })
                      }
                      className="px-4 py-2.5 rounded-lg border outline-none"
                      style={{
                        backgroundColor: `${colors.background}80`,
                        borderColor: colors.border,
                        color: colors.text,
                      }}
                    />
                  </div>
                </>
              )}
            </div>

            <div
              className="p-6 border-t flex justify-end gap-3"
              style={{ borderColor: colors.border }}
            >
              <button
                className="px-6 py-2.5 rounded-lg font-semibold"
                style={{
                  backgroundColor: colors.border,
                  color: colors.text,
                }}
                onClick={() => setShowMaintenanceModal(false)}
              >
                Cancel
              </button>

              <button
                className="px-6 py-2.5 rounded-lg text-white font-semibold"
                style={{ backgroundImage: colors.gradient }}
                onClick={
                  maintenanceForm.id
                    ? handleUpdateMaintenance
                    : handleAddMaintenance
                }
              >
                {maintenanceForm.id ? "Update" : "Add"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Maintenance Log Delete Model */}
      {showMaintenanceDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="backdrop-blur-sm rounded-xl border overflow-hidden w-full max-w-md"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
            }}
          >
            <div className="p-6">
              <h3
                className="text-lg font-semibold mb-4"
                style={{ color: colors.text }}
              >
                Are you sure you want to delete this maintenance log?
              </h3>

              <div className="flex justify-end gap-3">
                {/* Cancel */}
                <button
                  className="px-6 py-2.5 rounded-lg font-semibold"
                  style={{
                    backgroundColor: colors.border,
                    color: colors.text,
                  }}
                  onClick={() => setShowMaintenanceDeleteModal(false)}
                >
                  Cancel
                </button>

                {/* Confirm Delete */}
                <button
                  className="px-6 py-2.5 rounded-lg text-white font-semibold"
                  style={{
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  }}
                  onClick={confirmDeleteMaintenance}
                >
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/*Maintenance Delete Modal */}
      {showDeleteConfirmation && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="backdrop-blur-sm rounded-xl border overflow-hidden w-full max-w-md"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
            }}
          >
            <div className="p-6">
              <h3
                className="text-lg font-semibold mb-4"
                style={{ color: colors.text }}
              >
                Are you sure you want to delete?
              </h3>

              <div className="flex justify-end gap-3">
                {/* NO BUTTON */}
                <button
                  className="px-6 py-2.5 rounded-lg font-semibold text-base transition-all duration-300"
                  style={{
                    backgroundColor: colors.border,
                    color: colors.text,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = `${colors.primary}1A`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = colors.border)
                  }
                  onClick={() => setShowDeleteConfirmation(false)}
                >
                  No
                </button>

                <button
                  className="px-6 py-2.5 rounded-lg text-white font-semibold text-base transition-all duration-300"
                  style={{
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.boxShadow =
                      "0 20px 25px -5px rgba(239,68,68,0.3)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.boxShadow = "none")
                  }
                  onClick={confirmDelete}
                >
                  Yes
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Temp meter delete model */}
      {showDeleteConfirmation && (
        <div className="confirmation-modal">
          <h2>Are you sure you want to delete this Temp Meter?</h2>
          <button onClick={handleDeleteTempMeter}>Yes</button>
          <button onClick={() => setShowDeleteConfirmation(false)}>No</button>
        </div>
      )}
    </motion.div>
  );
}
